import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Plus, Hash, Trophy, Users, ChevronRight, Loader2 } from "lucide-react";
import QuickRulesCard from "../components/QuickRulesCard";

export default function Dashboard() {
    const nav = useNavigate();
    const { t } = useI18n();
    const [pools, setPools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [showJoin, setShowJoin] = useState(false);
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [betAmount, setBetAmount] = useState("");
    const [betCurrency, setBetCurrency] = useState("");
    const [betWinner, setBetWinner] = useState("");
    const [betInstructions, setBetInstructions] = useState("");
    const [joinCode, setJoinCode] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/pools/mine");
            setPools(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onCreate = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const parsedAmount = betAmount.trim() === "" ? null : Number(betAmount);
            const payload = {
                name,
                description: desc,
                bet_amount: Number.isFinite(parsedAmount) ? parsedAmount : null,
                bet_currency: betCurrency.trim(),
                bet_winner: betWinner.trim(),
                bet_instructions: betInstructions.trim(),
            };
            const { data } = await api.post("/pools", payload);
            toast.success(t("dash.toast.pool_created", { code: data.code }));
            setShowCreate(false);
            setName(""); setDesc("");
            setBetAmount(""); setBetCurrency(""); setBetWinner(""); setBetInstructions("");
            nav(`/pools/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const onJoin = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/pools/join", { code: joinCode });
            toast.success(t("dash.toast.joined", { name: data.name }));
            setShowJoin(false); setJoinCode("");
            nav(`/pools/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="dashboard-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--accent-hex)]">{t("dash.eyebrow")}</p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-[var(--text-primary)] mt-1">
                        {t("dash.title")}
                    </h1>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowJoin(true)}
                        data-testid="open-join-pool"
                        className="inline-flex items-center gap-2 border border-[var(--border-hover)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-4 py-2.5 rounded-md hover:border-white/60 transition-colors"
                    >
                        <Hash className="h-4 w-4" /> {t("dash.btn.join")}
                    </button>
                    <button
                        onClick={() => setShowCreate(true)}
                        data-testid="open-create-pool"
                        className="inline-flex items-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-2.5 rounded-md hover:bg-[var(--accent-hex-hover)] transition-colors"
                    >
                        <Plus className="h-4 w-4" /> {t("dash.btn.create")}
                    </button>
                </div>
            </div>

            <QuickRulesCard
                variant="dashboard"
                onCreateClick={() => setShowCreate(true)}
                onJoinClick={() => setShowJoin(true)}
            />

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" /></div>
            ) : pools.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[var(--border-base)] p-12 text-center bg-[var(--bg-card)]" data-testid="empty-pools">
                    <Trophy className="h-10 w-10 text-[var(--accent-hex)] mx-auto mb-3" />
                    <h2 className="font-heading text-2xl font-bold uppercase text-[var(--text-primary)]">{t("dash.empty.title")}</h2>
                    <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-md mx-auto">
                        {t("dash.empty.desc")}
                    </p>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pools-grid">
                    {pools.map((p) => (
                        <Link
                            to={`/pools/${p.id}`} key={p.id}
                            data-testid={`pool-card-${p.id}`}
                            className="block p-5 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] hover:border-[var(--accent-border)] hover:-translate-y-1 transition-all duration-200"
                        >
                            <div className="flex items-start justify-between">
                                <div className="font-heading text-2xl font-bold uppercase tracking-tight text-[var(--text-primary)] leading-tight">
                                    {p.name}
                                </div>
                                {p.is_admin && (
                                    <span className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-[var(--accent-hex)] text-[var(--accent-text)]">
                                        {t("nav.admin_badge")}
                                    </span>
                                )}
                            </div>
                            {(p.total_prize > 0) && (
                                <div className="mt-1 text-[10px] uppercase tracking-widest font-extrabold text-amber-400">
                                    {t("pool.prize_pot")}: {p.total_prize}{p.bet_currency ? ` ${p.bet_currency}` : ""}
                                </div>
                            )}
                            {p.description && <p className="text-sm text-[var(--text-secondary)] mt-2 line-clamp-2">{p.description}</p>}
                            <div className="mt-4 flex items-center justify-between text-xs">
                                <span className="inline-flex items-center gap-1 text-[var(--text-secondary)]">
                                    <Users className="h-3.5 w-3.5" /> {t("dash.members_count", { n: p.member_count })}
                                </span>
                                <span className="inline-flex items-center gap-1 font-mono text-[var(--accent-hex)] font-bold tracking-widest">
                                    {p.code} <ChevronRight className="h-4 w-4" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {/* CREATE MODAL */}
            {showCreate && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 pt-24 pb-8 overflow-y-auto" data-testid="create-modal" onClick={() => setShowCreate(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={onCreate} className="w-full max-w-md rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-6 space-y-4 my-8">
                        <h2 className="font-heading text-2xl font-bold uppercase text-[var(--text-primary)]">{t("dash.modal.new")}</h2>
                        <div>
                            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.name")}</label>
                            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="create-name"
                                className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.desc")}</label>
                            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} data-testid="create-desc"
                                className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                        </div>

                        {/* Friendly bet section */}
                        <div className="pt-3 mt-1 border-t border-[var(--border-base)] space-y-3" data-testid="create-bet-section">
                            <div>
                                <h3 className="text-sm font-bold uppercase tracking-wide text-[var(--accent-hex)]">{t("dash.modal.bet.title")}</h3>
                                <p className="text-xs text-[var(--text-secondary)] mt-1">{t("dash.modal.bet.hint")}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.bet.amount")}</label>
                                    <input value={betAmount} onChange={(e) => setBetAmount(e.target.value)} type="number" inputMode="decimal" min="0" step="any" data-testid="create-bet-amount"
                                        placeholder="200"
                                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.bet.currency")}</label>
                                    <input value={betCurrency} onChange={(e) => setBetCurrency(e.target.value.toUpperCase().slice(0, 6))} data-testid="create-bet-currency"
                                        placeholder="SEK"
                                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md uppercase tracking-widest focus:outline-none focus:border-[var(--accent-hex)]" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.bet.winner")}</label>
                                <input value={betWinner} onChange={(e) => setBetWinner(e.target.value)} data-testid="create-bet-winner"
                                    placeholder={t("dash.modal.bet.winner_ph")}
                                    className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.bet.instructions")}</label>
                                <textarea value={betInstructions} onChange={(e) => setBetInstructions(e.target.value)} rows={2} data-testid="create-bet-instructions"
                                    placeholder={t("dash.modal.bet.instructions_ph")}
                                    className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                            </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                            <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-[var(--border-hover)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-4 py-2.5 rounded-md">{t("dash.modal.cancel")}</button>
                            <button type="submit" disabled={busy} data-testid="create-submit" className="flex-1 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-2.5 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50">
                                {busy ? t("dash.modal.creating") : t("dash.modal.create")}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* JOIN MODAL */}
            {showJoin && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 pt-24 pb-8 overflow-y-auto" data-testid="join-modal" onClick={() => setShowJoin(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={onJoin} className="w-full max-w-md rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-6 space-y-4">
                        <h2 className="font-heading text-2xl font-bold uppercase text-[var(--text-primary)]">{t("dash.join.title")}</h2>
                        <div>
                            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.join.code")}</label>
                            <input required value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                data-testid="join-code-input"
                                placeholder="ABC234"
                                className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md font-mono tracking-widest text-lg uppercase focus:outline-none focus:border-[var(--accent-hex)]" />
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button type="button" onClick={() => setShowJoin(false)} className="flex-1 border border-[var(--border-hover)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-4 py-2.5 rounded-md">{t("dash.modal.cancel")}</button>
                            <button type="submit" disabled={busy} data-testid="join-submit" className="flex-1 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-2.5 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50">
                                {busy ? t("dash.join.joining") : t("dash.join.submit")}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
